import { ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import ShowCodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { Color } from '@tiptap/extension-color';
import ShowImage from '@tiptap/extension-image';
import ShowLink from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import dayjs from 'dayjs';
import { lowlight } from 'lowlight/lib/common';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { fetchDetailPost } from '../../../api/post';
import styles from './DetailPost.module.css';

const DetailPost = () => {
    const { id } = useParams();
    const [post, setPost] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const { onTitleChange } = useOutletContext();
    const { t, i18n } = useTranslation();
    const language = i18n.resolvedLanguage?.startsWith('zh') ? 'zh' : 'en';
    const navigate = useNavigate();

    const editor = useEditor({
        extensions: [
            StarterKit.configure({ codeBlock: false, link: false }),
            // Existing articles may still contain Base64 image nodes.
            ShowImage.configure({ allowBase64: true }),
            ShowLink,
            ShowCodeBlockLowlight.configure({ lowlight }),
            TextStyle,
            Color,
        ],
        content: '<p></p>',
        editable: false,
    });

    useEffect(() => {
        let active = true;

        const loadArticle = async () => {
            setLoading(true);
            setError(false);
            try {
                const result = await fetchDetailPost(id, language);
                const article = result.data.data;
                if (active) {
                    setPost(article);
                    onTitleChange(article.title);
                    if (article.id && String(article.id) !== String(id)) {
                        navigate(`/post/${article.id}`, { replace: true });
                    }
                }
            } catch (loadError) {
                if (active) setError(true);
            } finally {
                if (active) setLoading(false);
            }
        };

        loadArticle();
        return () => { active = false; };
    }, [id, language, navigate, onTitleChange]);

    useEffect(() => {
        if (!editor || !post?.content) return;
        try {
            editor.commands.setContent(JSON.parse(post.content));
        } catch (parseError) {
            editor.commands.setContent(post.content);
        }
    }, [editor, post?.content]);

    if (loading) return <div className={styles.state}>{t('writing.loadingArticle')}</div>;
    if (error) return <div className={styles.state}>{t('writing.detailError')}</div>;

    return (
        <article className={styles.article}>
            {post.lng && post.lng !== language && (
                <div className={styles.languageNotice}>{t('writing.translationMissing')}</div>
            )}
            <div className={styles.meta}>
                <span><UserOutlined /> {post.author || 'Yi Chen'}</span>
                <span><ClockCircleOutlined /> {dayjs(post.date).format('YYYY-MM-DD')}</span>
            </div>
            <EditorContent editor={editor} className={styles.TiptapEditor} />
        </article>
    );
};

export default DetailPost;
